// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimeSelectModel.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable } from 'mobx';
import Moment from 'moment';
import { isEmpty } from 'lodash/fp';
import { parseDateTime, isDateTimeValid, getNowWithoutTime, isDateValid, DEFAULT_TIME_VALUE } from 'share/utils/Izenda.DateTimeUtils';

export default class DateTimePickerModel {
  @observable inputValue: string = '';
  @observable dateTime: Moment = null;

  // Hold initial value when set data
  initialValue: string = '';

  // DOM Node of the input
  inputDOMNode: Object = null;

  /**
   * Set formats
   * @param {string} displayFormat
   * @param {string} storeFormat
   */
  setFormats(displayFormat: string, storeFormat: string) {
    this.displayFormat = displayFormat;
    this.storeFormat = storeFormat;
    this.syncInputValueWithDateTime();
  }

  /**
   * Set input DOM Node
   * @param {Object} inputNode
   */
  setInputDOMNode(inputNode: Object) {
    inputNode && (this.inputDOMNode = inputNode.input);
  }

  /**
   * Focus the input
   */
  focusInput() {
    this.inputDOMNode && this.inputDOMNode.focus();
  }

  /**
   * Sync input value with current date time
   */
  syncInputValueWithDateTime() {
    this.inputValue = isDateTimeValid(this.dateTime) ? this.dateTime.format(this.displayFormat) : '';
  }

  /**
   * Commit current input value when user blur or enter
   * @param {string} value
   */
  commitCurrentInputValue() {
    if (isEmpty(this.inputValue)) {
      return false;
    }

    const newDateTime = this.generateNewDatetime();
    return isDateTimeValid(newDateTime) ? this.setDateTime(newDateTime) : this.setToValidDateTime();
  }

  /**
   * Set to default time value {00:00:00} if input value only date in case of datetime.
   */
  generateNewDatetime() {
    let newDateTime = parseDateTime(this.inputValue, [this.displayFormat, this.storeFormat]);
    if (!isDateTimeValid(newDateTime) && isDateValid(this.inputValue, this.displayFormat)) {
      this.inputValue = `${this.inputValue} ${DEFAULT_TIME_VALUE}`;
      newDateTime = parseDateTime(this.inputValue, [this.displayFormat, this.storeFormat]);
    }
    return newDateTime;
  }

  /**
   * Set to valid date time
   */
  setToValidDateTime() {
    if (isDateTimeValid(this.dateTime)) {
      this.syncInputValueWithDateTime();
      return false;
    }
    return this.setDateTime(getNowWithoutTime());
  }

  /**
   * Set input value
   * @param {string} value
   * @returns {boolean} Indicates the date time was changed
   */
  changeInputValue(value: string) {
    this.inputValue = value || '';
    if (isEmpty(this.inputValue)) {
      return this.setDateTime(null);
    }
    const newDateTime = Moment(this.inputValue, this.displayFormat, true);
    return isDateTimeValid(newDateTime) && this.setDateTime(newDateTime);
  }


  /**
   * Get date time string in display format
   * @param {Moment} dateTime 
   */
  getDisplayValue(dateTime: Moment) {
    const { displayFormat } = this;
    return isDateTimeValid(dateTime) ? dateTime.format(displayFormat) : '';
  }

  /**
   * Set initial data
   * @param {string} value 
   */
  setData(value: string) {
    const newDateTime = parseDateTime(value, [this.displayFormat, this.storeFormat]);
    this.setDateTime(newDateTime);
    this.initialValue = this.getSavingValue();
  }

  /**
   * Check if current value had changed compare to initial value
   */
  hadValueChanged() {
    return this.initialValue !== this.getSavingValue();
  }

  /**
   * Set date time
   * @param {Moment} dateTime
   * @returns A boolean value indicates the date time was changed
   */
  setDateTime(dateTime: Moment) {
    const currentDateTimeString = this.getDisplayValue(this.dateTime);
    const newDateTimeString = this.getDisplayValue(dateTime);

    if (currentDateTimeString !== newDateTimeString) {
      this.dateTime = dateTime;
      this.syncInputValueWithDateTime();
      return true;
    }
    return false;
  }

  /**
   * Get saving value
   */
  getSavingValue() {
    const { storeFormat } = this;
    return isDateTimeValid(this.dateTime) ? this.dateTime.format(storeFormat) : '';
  }

  /**
   * Add date time
   * @param {number} span
   * @param {string} unit
   */
  addDateTime(span: number, unit: string) {
    this.setToValidDateTime();
    this.dateTime.add(span, unit);
  }

  /**
   * Subtract date time
   * @param {number} span
   * @param {string} unit
   */
  subtractDateTime(span: number, unit: string) {
    this.setToValidDateTime();
    this.dateTime.subtract(span, unit);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.DateTimePickerModel.js